import { PurchaseDetails } from '../types/purchase';

type EventParams = Record<string, unknown>;

const SIGNUP_EVENT_STORAGE_PREFIX = 'analytics_signup_event:';
const claimedSignupEvents = new Set<string>();
const claimedPurchases = new Set<string>();
let analyticsUserId: string | null = null;
const PENDING_AUTH_KEY = 'analytics_pending_auth';
const isAuthCallback = (): boolean => typeof window !== 'undefined' && (
  window.location.pathname.startsWith('/auth/callback/')
  || window.location.pathname === '/github/repo-auth/callback'
);

const pushDataLayerEvent = (eventName: string, params?: EventParams): void => {
  if (typeof window === 'undefined') return;

  const eventParams = analyticsUserId
    ? { ...params, user_id: analyticsUserId }
    : params;

  window.dataLayer = window.dataLayer || [];
  // GTM merges object variables across events unless explicitly cleared.
  window.dataLayer.push({
    ecommerce: null, eventModel: null, transaction_id: null,
    value: null, currency: null, items: null, event_id: null,
  });
  window.dataLayer.push({
    ...eventParams,
    event: eventName,
    // The published web GTM container forwards eventModel through its
    // server-side GA4 route. Keep a flat copy for the other GTM tags.
    eventModel: eventParams,
    ecommerce: eventName === 'purchase' ? params : null,
  });
};

export const setAnalyticsUserId = (userId?: string): void => {
  analyticsUserId = userId || null;

  if (typeof window === 'undefined') return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ user_id: analyticsUserId });
};

// ─── Generic Event ───────────────────────────────────────────
type GaEventListener = (name: string, params?: EventParams) => void;
const gaListeners: GaEventListener[] = [];

export const onGaEvent = (fn: GaEventListener): (() => void) => {
  gaListeners.push(fn);

  return () => { gaListeners.splice(gaListeners.indexOf(fn), 1); };
};

export const trackEvent = (eventName: string, params?: EventParams): void => {
  pushDataLayerEvent(eventName, params);
  gaListeners.forEach((fn) => fn(eventName, params));
};

const trackServerRoutedEvent = (eventName: string, params?: EventParams): void => {
  pushDataLayerEvent(eventName, params);
  gaListeners.forEach((fn) => fn(eventName, params));
};

const claimSignupEvent = (signupEventId: string): boolean => {
  if (claimedSignupEvents.has(signupEventId)) return false;

  const storageKey = `${SIGNUP_EVENT_STORAGE_PREFIX}${signupEventId}`;

  try {
    if (typeof window !== 'undefined' && window.localStorage.getItem(storageKey)) return false;
    if (typeof window !== 'undefined') window.localStorage.setItem(storageKey, '1');
  } catch {
    // The in-memory guard still prevents duplicate React effects when browser
    // storage is unavailable.
  }

  claimedSignupEvents.add(signupEventId);

  return true;
};

// ─── Auth Events ─────────────────────────────────────────────
export const trackLogin = (method: string): void => {
  trackEvent('login', { method });
};

export const trackSignUp = (method: string, signupEventId: string): void => {
  if (!signupEventId || !claimSignupEvent(signupEventId)) return;

  trackServerRoutedEvent('sign_up', {
    method,
    signup_event_id: signupEventId,
  });
};

export const trackLogout = (): void => {
  trackEvent('logout');
};

export const trackOAuthLogin = (provider: string): void => {
  trackEvent('login', { method: `oauth_${provider}` });
};

// eslint-disable-next-line max-statements
export const trackOAuthAuthOutcome = (
  provider: string,
  isNewUser: boolean,
  signupEventId?: string,
): void => {
  if (isAuthCallback()) {
    // GTM intentionally does not run on token-bearing callback URLs. Replay on
    // the destination page; do not claim a signup before it can be delivered.
    try {
      sessionStorage.setItem(PENDING_AUTH_KEY, JSON.stringify({
        provider, isNewUser, signupEventId, createdAt: Date.now(),
      }));
    } catch {
      // Analytics must not prevent authentication if storage is unavailable.
    }

    return;
  }

  if (!isNewUser) {
    trackOAuthLogin(provider);

    return;
  }

  if (!signupEventId) {
    console.warn('Skipping sign_up analytics because the backend did not provide an event ID');

    return;
  }

  trackSignUp(`oauth_${provider}`, signupEventId);
};

// eslint-disable-next-line max-statements
export const flushPendingAuthAnalytics = (): void => {
  if (!analyticsUserId || isAuthCallback()) return;
  try {
    const raw = sessionStorage.getItem(PENDING_AUTH_KEY);

    if (!raw) return;
    const pending = JSON.parse(raw);

    sessionStorage.removeItem(PENDING_AUTH_KEY);
    if (typeof pending.provider !== 'string' || typeof pending.isNewUser !== 'boolean'
      || typeof pending.createdAt !== 'number' || Date.now() - pending.createdAt > 15 * 60 * 1000) return;

    trackOAuthAuthOutcome(pending.provider, pending.isNewUser, pending.signupEventId);
  } catch {
    // Ignore corrupt/blocked browser storage rather than interrupting login.
  }
};

// ─── Chat Events ─────────────────────────────────────────────
export const trackChatMessage = (): void => {
  trackEvent('chat_message_sent');
};

export const trackFirstMessage = (): void => {
  trackEvent('first_message_sent');
};

export const trackBuildStart = (): void => {
  trackEvent('build_started');
};

// ─── Planning Page Button Events ─────────────────────────────
export const trackViewAgentResult = (agentLabel: string): void => {
  trackEvent('view_agent_result', { agent_label: agentLabel });
};

export const trackReviewClick = (): void => {
  trackEvent('review_click');
};

export const trackStartBuildingClick = (source: 'chat_message' | 'review_wizard' | 'agent_pill'): void => {
  trackEvent('start_building_click', { ui_source: source });
};

// ─── Project Events ──────────────────────────────────────────
export const trackProjectCreate = (): void => {
  trackEvent('project_created');
};

export const trackProjectOpen = (): void => {
  trackEvent('project_opened');
};

export const trackProjectDelete = (type: 'single' | 'bulk'): void => {
  trackEvent('project_deleted', { delete_type: type });
};

// ─── Billing Events ──────────────────────────────────────────
export const trackPricingClick = (planName: string): void => {
  trackEvent('pricing_plan_selected', { plan_name: planName });
};

// ─── TrafficJunky CPA Conversion ─────────────────────────────
const buildPixel = (id: string, src: string): HTMLImageElement => {
  const img = Object.assign(document.createElement('img'), { id, src, width: 1, height: 1 });

  Object.assign(img.style, { border: '0', position: 'absolute', left: '-9999px' });

  return img;
};

export const trackTrafficJunkyCPA = (transactionId?: string): void => {
  if (!window.__load8080Marketing || !transactionId) return;

  const cb = Math.floor(Math.random() * 1e9);
  // Preserve the existing advertiser CPA goal value; it is not GA4 revenue.
  const src = `https://ads.trafficjunky.net/ct?a=1000563181&member_id=1008578851&cb=${cb}&cti=${encodeURIComponent(transactionId)}&ctv=5.00&ctd=purchase`;

  document.body.appendChild(buildPixel('1000563181_cpa_testing', src));
};

// ─── MagSrv Server-to-Server Conversion ──────────────────────
const MAGSRV_STORAGE_KEY = 'magsrv_ct';

export const captureMagSrvToken = (): void => {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('conversions_tracking');

  if (token) {
    localStorage.setItem(MAGSRV_STORAGE_KEY, token);
  }
};

export const trackMagSrvConversion = (value?: number): void => {
  if (!window.__load8080Marketing || !value || !Number.isFinite(value) || value <= 0) return;

  const tag = localStorage.getItem(MAGSRV_STORAGE_KEY);

  if (!tag) return;

  const src = `https://s.magsrv.com/tag.php?goal=4cb3d2f7ad06f7349fbbf9a21fd46c0c&tag=${encodeURIComponent(tag)}&value=${value.toFixed(2)}`;

  document.body.appendChild(buildPixel('magsrv_conversion', src));

  localStorage.removeItem(MAGSRV_STORAGE_KEY);
};

// ─── Canonical purchase event (GTM maps it to each destination) ───────────────
// eslint-disable-next-line max-statements
export const trackPurchase = (params: PurchaseDetails): void => {
  if (!params.transaction_id || !Number.isFinite(params.value) || params.value <= 0 || !params.currency) {
    console.warn('Skipping invalid purchase analytics payload', params.transaction_id);

    return;
  }

  const key = `analytics_purchase:${params.transaction_id}`;

  if (claimedPurchases.has(key)) return;
  try {
    if (sessionStorage.getItem(key)) return;
  } catch { /* In-memory guard still handles repeated React effects. */ }

  trackServerRoutedEvent('purchase', {
    transaction_id: params.transaction_id,
    value: params.value,
    currency: params.currency.toUpperCase(),
    purchase_type: params.purchase_type,
    payment_provider: params.payment_provider,
    items: params.items,
    event_id: params.transaction_id,
  });
  claimedPurchases.add(key);
  try { sessionStorage.setItem(key, '1'); } catch { /* Storage is optional. */ }
};

export const trackVerifiedPurchase = (purchase?: PurchaseDetails): void => {
  if (purchase) trackPurchase(purchase);
};
